import React, { FunctionComponent, useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'react-string-format';
import jsonpath from 'jsonpath';
import { Box, FormHelperText, Grid, Hidden, makeStyles, Theme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import gray from '@material-ui/core/colors/grey';
import { FormFieldProps } from '../FormFieldDisplay';
import { DropDownField } from '../../../../store/modules/application/types';
import { isArray, isObject, forEach, orderBy, get } from 'lodash';
import { getRequiredScopes } from '../../../../authentication';
import { Api } from '../../../../store/services/services';
import { useAuthenticationContext } from '../../../../authentication';
import { composeParamsForSearch } from '../../../../utils/query';
import AutoCompleteSelect from '../../../AutoCompleteSelect';
import { extractValuesFromRegex, getFormattedField } from 'src/utils/utils';

const useStyles = makeStyles((theme: Theme) => ({
    detailNodeLabelSm: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'left',
        justifyContent: 'right',
        marginRight: 30,
        lineHeight: '21px',
    },
    detailNodeLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: gray[800],
        textAlign: 'right',
        justifyContent: 'right',
        marginRight: 30,
        lineHeight: '21px',
    },
    detailNodeValue: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
    },
    autoCompleteRoot: {
        marginBottom: '5px',
    },
}));

const DropDownFormField: FunctionComponent<FormFieldProps> = (props) => {
    const {
        formField,
        value,
        error,
        readOnly,
        handleInputChange,
        formData,
        isCustomStyle,
        handleSelectedInspector,
    } = props;
    const authContext = useAuthenticationContext();

    const [searchURL, setSearchURL] = useState<string>('');
    const [searchedResults, setSearchedResults] = useState<[]>();
    const [loading, setLoading] = useState<boolean>(false);
    const [choices, setChoices] = useState<DropDownField[]>([]);
    const [textInputValue, setTextInputValue] = useState<string>('');
    const [searchQueryString, setSearchQueryString] = useState<string>('');
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    });
    const [searchResults, setSearchResults] = useState<DropDownField[]>([]);

    const classes = useStyles();

    const textValue: string = typeof value === 'string' ? value : '';
    const licenseId = value?.license?.id;
    useEffect(() => {
        if (!textValue && formField?.fieldMapping?.targetPath === '$.inspector.id' && licenseId) {
            const selectedLicense = { label: value.name, value: licenseId };
            setChoices((choices) => [...choices, selectedLicense]);
        }
    }, [textValue, value, formField, licenseId]);

    const { scopes } = authContext;

    let params: any = [];

    if (formField.api?.queryParams) {
        forEach(formField.api?.queryParams, (value: any) => {
            if (value.hasOwnProperty('fact') && formData) {
                params.push(formData![value.fact]);
            } else params.push(value);
        });
    }

    const generateQueryString = (queryParams: any, apiUrl: string) => {
        let queryString = composeParamsForSearch(queryParams);
        let url: string = apiUrl;
        url = `${url}?${queryString}`;
        return { queryString, url };
    };

    const getFieldChoicesForSearch = useCallback(
        async (url) => {
            let choices: DropDownField[] = [];

            if (url) {
                const scope = getRequiredScopes(formField?.search?.url!);
                if (scope) {
                    const token = await authContext
                        .getAccessToken({ scopes: [scopes[scope]] })
                        .then((response) => response.accessToken);
                    mounted?.current && setLoading(true);
                    let data = await Api.getChoices(url, token);
                    mounted?.current && setLoading(false);
                    if (isArray(data)) {
                        if (formField?.search?.resultMapping) {
                            choices = data.map((choice) => {
                                let choiceMap: any = {};
                                forEach(
                                    formField?.search?.resultMapping!,
                                    (mapValue: string | { template: string }, mapKey) => {
                                        if (typeof mapValue === 'object') {
                                            const templateFormat = getFormattedField(mapValue.template);
                                            const mappedFields = extractValuesFromRegex(mapValue.template);
                                            const computedValues = mappedFields.flatMap((targetPath) => {
                                                return targetPath.split('.').reduce((resultObj, value) => {
                                                    return resultObj && resultObj[value] ? resultObj[value] : '';
                                                }, choice);
                                            });
                                            choiceMap[mapKey] = format(templateFormat, ...computedValues);
                                        } else choiceMap[mapKey] = get(choice, mapValue);
                                    },
                                );

                                return choiceMap;
                            });
                        } else if (data.some((choice) => !isObject(choice))) {
                            choices = data.map((choice) => {
                                return {
                                    label: choice,
                                    value: choice,
                                };
                            });
                        } else {
                            choices = data;
                        }
                        setSearchedResults(data as []);
                    }
                    if (choices && mounted.current) {
                        choices = choices.map((choice) => {
                            choice.value = 'search->' + choice.value;
                            return choice;
                        });
                        setSearchResults(choices);
                    }
                }
            } else {
                if (formField.choices && mounted.current) {
                    setSearchResults(orderBy(formField.choices, 'label', 'asc'));
                }
            }
        },
        [authContext, formField, scopes],
    );

    useEffect(() => {
        if (textInputValue && textInputValue.trim().length > 2) {
            if (formField?.search?.url) {
                if (searchQueryString.includes('$value')) {
                    let url = searchURL.replace('$value', textInputValue);
                    url = url.replace(/(\?")/g, '?').replace(/("=)/g, '=');
                    getFieldChoicesForSearch(url);
                }
            }
        }
        // eslint-disable-next-line
    }, [textInputValue]);

    useEffect(() => {
        if (formField?.search?.url) {
            let { queryString, url } = generateQueryString(formField?.search?.queryParams, formField?.search?.url);

            if (url && queryString.includes('_context')) {
                const path = jsonpath.value(formField?.search?.queryParams, '$..path');
                const value = jsonpath.value(formData?._context, path);
                url = url.replace(path, value).replace(/_context/, '');
                queryString = queryString.replace(path, value).replace(/_context/, '');
            }
            url = url.replace(/(\?")/g, '?').replace(/("=)/g, '=');
            setSearchQueryString(queryString);
            setSearchURL(url);
            getFieldChoicesForSearch(url);
        }
        // eslint-disable-next-line
    }, [formField, formData?._context]);

    const getFieldChoices = useCallback(async () => {
        let choices: DropDownField[] = [];

        if (formField.api) {
            let url: string = formField.api.url;

            const scope = getRequiredScopes(url);

            if (formField.api.queryParams) {
                const queryParams = composeParamsForSearch(formField.api.queryParams);

                url = `${url}?${queryParams}`;
            }

            if (scope) {
                const token = await authContext
                    .getAccessToken({ scopes: [scopes[scope]] })
                    .then((response) => response.accessToken);
                setLoading(true);
                let data = await Api.getChoices(url, token);
                setLoading(false);
                if (formField.api.path) {
                    data = jsonpath.value(data, formField.api.path);
                }

                if (isArray(data)) {
                    if (formField.api.resultMapping) {
                        choices = data.map((choice) => {
                            let choiceMap: any = {};

                            forEach(formField?.api?.resultMapping!, (mapValue, mapKey) => {
                                choiceMap[mapKey] = choice[mapValue];
                            });

                            return choiceMap;
                        });
                    } else if (data.some((choice) => !isObject(choice))) {
                        choices = data.map((choice) => {
                            return {
                                label: choice,
                                value: choice,
                            };
                        });
                    } else {
                        choices = data;
                    }
                }
                if (choices) {
                    setChoices((choice) => orderBy([...choice, ...choices], 'label', 'asc'));
                }
            }
        } else {
            if (formField.choices) {
                setChoices(orderBy(formField.choices, 'label', 'asc'));
            }
        }
    }, [authContext, formField, scopes]);

    useEffect(() => {
        getFieldChoices();
        // eslint-disable-next-line
    }, [...params]);

    const onTextInputValueChange = (value: string) => {
        setTextInputValue(value);
    };

    const selectedValueCondition =
        formField.field === 'inspector' && licenseId
            ? (choice: DropDownField) => choice.value === licenseId
            : (choice: DropDownField) => value === choice.value;
    const selectedValue = [...choices, ...searchResults]?.find(selectedValueCondition);
    const Loader = formField.api && <Skeleton variant="text" width="100%" />;
    return (
        <Grid container alignItems={readOnly ? 'flex-start' : 'center'}>
            <Grid item xs={isCustomStyle ? 3 : 6}>
                <Hidden smUp={true}>
                    <Box component="div" className={classes.detailNodeLabelSm}>
                        {formField.label}{' '}
                    </Box>
                </Hidden>
                <Hidden xsDown={true}>
                    <Box component="div" className={classes.detailNodeLabel}>
                        {formField.label}{' '}
                    </Box>
                </Hidden>
            </Grid>
            <Grid item xs={isCustomStyle ? 9 : 6}>
                <Box component="div" className={classes.detailNodeValue}>
                    {!readOnly && (
                        <>
                            <AutoCompleteSelect
                                //data is sorted alphabetically (ascending order)
                                data={orderBy(
                                    [...choices, ...searchResults],
                                    (elem) => elem.label.toLowerCase(),
                                    'asc',
                                )}
                                inputSize={'small'}
                                variant={'outlined'}
                                error={error?.error}
                                value={selectedValue?.value ? selectedValue : null}
                                onChange={(data: DropDownField | null) => {
                                    searchedResults?.length &&
                                        handleSelectedInspector &&
                                        handleSelectedInspector(
                                            searchedResults?.find(
                                                (result: any) => result.id === data?.value.replace('search->', ''),
                                            ),
                                        );
                                    const dropDownData = data !== null ? data.value : null;
                                    handleInputChange(formField.field, dropDownData);
                                }}
                                onTextInputValueChange={onTextInputValueChange}
                                classes={{
                                    root: classes.autoCompleteRoot,
                                }}
                                ariaLabel={formField.label}
                                instruction={
                                    !!formField?.search
                                        ? !!formField?.search?.instructions
                                            ? formField?.search?.instructions
                                            : 'Select from the list or search for a record'
                                        : ''
                                }
                            />
                            {error?.messages?.map((msg, index) => (
                                <FormHelperText key={index} error={true}>
                                    {msg}
                                </FormHelperText>
                            ))}
                        </>
                    )}
                    {readOnly && (
                        <Box component="div" className={classes.detailNodeValue}>
                            {!loading ? selectedValue?.label : Loader}
                        </Box>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default DropDownFormField;
